import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
console.log('API_BASE_URL:', API_BASE_URL);

// Helper function to parse JWT token
const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

// Helper to check if token is expired
const isTokenExpired = (token) => {
  const decodedToken = parseJwt(token);
  if (!decodedToken) return true;
  return decodedToken.exp * 1000 < Date.now();
};

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token and checking token expiry
apiClient.interceptors.request.use(
  async (config) => {
    // Get the current token
    let token = localStorage.getItem('jms_token');

    // Check if token exists and is expired
    if (token && isTokenExpired(token)) {
      // We need to dynamically import the store to avoid circular dependency
      const authStore = (await import('../store/authStore')).default;

      // Try to refresh the token using the store's refreshToken method
      const refreshed = await authStore.getState().refreshToken();

      // If refresh was successful, get the new token
      if (refreshed) {
        token = localStorage.getItem('jms_token');
      } else {
        // If refresh failed, clear the token
        token = null;
      }
    }

    // Add token to request headers if it exists
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      //console.log('Adding token to request headers:', config.url);
    } else {
      console.log('No token found for request:', config.url);
    }

    //console.log('Request config:', config.method, config.url, config.headers);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
apiClient.interceptors.response.use(
  (response) => {
    // console.log('API Response success:', response.config.method, response.config.url, response.status);
    return response;
  },
  async (error) => {
    //console.error('API Response error:', error.config?.method, error.config?.url, error.response?.status, error.message);
    const originalRequest = error.config;

    // Skip token refresh for login, register, and public endpoints
    const publicEndpoints = [
      '/auth/login',
      '/auth/register',
      '/editorial-board',  // Public editorial board endpoints
      '/journals-public',   // Public journal endpoints
      '/publications',      // Public publications
      '/committee'          // Public committee members
    ];
    const isPublicEndpoint = publicEndpoints.some(endpoint => originalRequest.url?.includes(endpoint));

    // Handle token expiration and refresh
    if (error.response?.status === 401 && !originalRequest._retry && !isPublicEndpoint) {
      originalRequest._retry = true;

      try {
        // We need to dynamically import the store to avoid circular dependency
        const authStore = (await import('../store/authStore')).default;

        // Try to refresh the token using the store's refreshToken method
        const refreshed = await authStore.getState().refreshToken();

        if (refreshed) {
          // Get the new token
          const token = localStorage.getItem('jms_token');

          // Update the Authorization header
          originalRequest.headers.Authorization = `Bearer ${token}`;

          // Retry the original request
          return apiClient(originalRequest);
        } else {
          // If refresh failed, redirect to login
          window.location.href = '/';
        }
      } catch (refreshError) {
        console.error('Error during token refresh:', refreshError);
        // If there's an error during refresh, logout and redirect
        const authStore = (await import('../store/authStore')).default;
        authStore.getState().logout();
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

export { endpoints } from './endpoints';
